import type { Job, Nurse } from "./models";

/**
 * A staff member's primary job — the first entry of their ORDERED job list.
 *
 * Ordering carries the meaning: `onSetPrimaryJob` (profile Professional Info) moves the chosen job to
 * the front of `jobIds`, so "primary" is positional rather than a flag. `displayName` is what users
 * named it; `name` is the fallback for jobs created before display names existed.
 *
 * Returns `undefined` — not a role, not a placeholder — for a staff member with no jobs assigned, so
 * callers decide what an unassigned employee should read as in their own surface.
 */
export function getPrimaryJobLabel(nurse: Pick<Nurse, "jobs"> | undefined): string | undefined {
	const primary = nurse?.jobs?.[0];
	if (!primary) return undefined;
	return primary.displayName || primary.name;
}

/**
 * A staff member's primary job entity — same "first entry of the ordered job list" rule as
 * {@link getPrimaryJobLabel}, but returns the `Job` itself rather than its display label.
 *
 * Returns `null` — not `undefined` — for a staff member with no jobs assigned, matching this
 * codebase's convention for "this rich entity may not resolve" (`T | null`, e.g.
 * `resolveLocationForCoverageRow`, `CertPrimaryJobChangePreviewRow.currentJob`).
 */
export function getPrimaryJob(nurse: Pick<Nurse, "jobs"> | undefined): Job | null {
	return nurse?.jobs?.[0] ?? null;
}

/**
 * Jobs in display-name order, with `id` as a second key so the comparator is **total**: two
 * distinct jobs sharing a display name always come out in the same order rather than whichever
 * order they arrived in. Display-name uniqueness is not enforced — `job.name` carries the unique
 * constraint, and `useCreateJob` lets an admin create a second job reading the same — so the tie
 * is reachable, and leaving it input-dependent made every list holding such a pair unstable.
 */
export function sortJobsByDisplayName(jobs: readonly Job[]): Job[] {
	return [...jobs].sort((a, b) => a.displayName.localeCompare(b.displayName) || a.id.localeCompare(b.id));
}

function getEnabledJobsByIds(enabledJobs: readonly Job[], jobIds: readonly string[]): Job[] {
	const jobById = new Map(enabledJobs.map((job) => [job.id, job]));
	return jobIds.map((id) => jobById.get(id)).filter((job): job is Job => job !== undefined);
}

export function getEnabledNurseJobs(nurse: Pick<Nurse, "jobs" | "jobIds">, enabledJobs: readonly Job[]): Job[] {
	if (nurse.jobIds?.length) return getEnabledJobsByIds(enabledJobs, nurse.jobIds);
	if (nurse.jobs?.length)
		return getEnabledJobsByIds(
			enabledJobs,
			nurse.jobs.map((job) => job.id),
		);
	// DEFAULT: a nurse with no job assignments has no nurse-specific enabled jobs; callers that
	// intentionally allow all enabled jobs for unassigned nurses handle that fallback themselves.
	return [];
}

/**
 * Staff-per-job tallies keyed by job **id** — the value a job option actually carries, so two jobs
 * that happen to share a display name can never merge into one count.
 *
 * "Holds the job" follows the same precedence {@link getEnabledNurseJobs} documents: `jobIds` when
 * the employee has any, otherwise the ids of the hydrated `jobs`. Each employee is counted at most
 * once per job — a duplicated id in `jobIds` must not inflate popularity.
 *
 * Takes an **already-filtered** roster. Deciding which employees count is department policy (see
 * `useStaffCountByJobId`, which drops Inactive staff), not arithmetic; keeping it out leaves this
 * function pure and directly testable.
 */
export function buildStaffCountByJobId(nurses: readonly Pick<Nurse, "jobs" | "jobIds">[]): Map<string, number> {
	const counts = new Map<string, number>();
	for (const nurse of nurses) {
		// DEFAULT: `jobs ?? []` — an employee with neither list holds no job, so they contribute to no
		// count. That is what an unassigned employee means, and it matches `getEnabledNurseJobs` above.
		const heldJobIds = nurse.jobIds?.length ? nurse.jobIds : (nurse.jobs ?? []).map((job) => job.id);
		for (const jobId of new Set(heldJobIds)) {
			counts.set(jobId, (counts.get(jobId) ?? 0) + 1);
		}
	}
	return counts;
}

/**
 * Jobs ordered by popularity — how many of the department's staff hold each job — most-held first,
 * ties broken alphabetically by `displayName`.
 *
 * A job missing from `staffCountByJobId` reads as 0, so "nobody holds this job" is decided by the
 * same comparison as "fewer people hold this job": zero-staff jobs land last, alphabetical among
 * themselves, with no special case.
 *
 * The comparator is **total** — count, then display name, then id (unique per job) — so the same
 * jobs always come out in the same order no matter what order they went in, without relying on
 * `Array.prototype.sort` being stable.
 */
export function sortJobsByPopularity(jobs: readonly Job[], staffCountByJobId: ReadonlyMap<string, number>): Job[] {
	// DEFAULT: `?? 0` — a job absent from the tally is one nobody holds. Zero is the real answer here,
	// not a papered-over lookup miss: it is what puts those jobs last, as documented above.
	const staffCount = (job: Job) => staffCountByJobId.get(job.id) ?? 0;
	return [...jobs].sort((a, b) => staffCount(b) - staffCount(a) || a.displayName.localeCompare(b.displayName) || a.id.localeCompare(b.id));
}
